<template>
    <div class="notification-push-v3-container">
        <div class="ppv2-bg-white dark:ppv2-bg-dark ppv2-rounded-3xl ppv2-p-6">
            <Header :title="$t('notifications_push.title')" />

            <DataTable
                :key="datatableKey"
                :columns="tableColumns"
                :items="items"
                :current-page="currentPage"
                :total-pages="totalPages"
                :enable-multi-select="false"
                :enable-drag-drop="false"
                @page-changed="handlePageChange"
                :perPage="perPage"
                @per-page-changed="handlePerPageChange"
                @sort="onSort"
            >
                <!-- Toolbar -->
                <template #toolbar>
                    <div class="ppv2-flex ppv2-flex-wrap ppv2-items-center" :style="{ gap: normalizeSize(16, true) }">
                        <div class="ppv2-flex-1">
                            <div class="ppv2-relative ppv2-gap-3 ppv2-flex ppv2-items-center">
                                <!-- Search -->
                                <Search v-model="searchQuery" :emitGlobal="false" @search-updated="onSearch" />

                                <!-- Statut filter -->
                                <FilterByType
                                    :label="$t('notifications_push.status.all')"
                                    :options="statusOptions"
                                    :selected="statusFilter"
                                    icon="Funnel"
                                    @filter-selected="handleStatusChange"
                                />
                            </div>
                        </div>

                        <!-- Add button -->
                        <BaseButton
                            variant="purple"
                            :custom-height="40"
                            :custom-font-size="14"
                            custom-class="ppv2-gap-3 ppv2-pl-4 ppv2-pr-[20px]"
                            @click="goToCreate"
                        >
                            <Plus :size="17" />
                            <span class="poppinsRegular">{{ $t('notifications_push.add') }}</span>
                        </BaseButton>
                    </div>
                </template>

                <!-- Custom cell: title_notif -->
                <template #column-title_notif="{ item }">
                    <div v-html="item.title_notif" class="noMarginInside"></div>
                </template>

                <!-- Custom cell: desc_notif -->
                <template #column-desc_notif="{ item }">
                    <div v-html="item.desc_notif" class="noMarginInside"></div>
                </template>

                <template #column-send="{ item }">
                    <span
                        class="ppv2-px-2 ppv2-py-1 ppv2-rounded-full ppv2-text-xs"
                        :class="item.send ? 'ppv2-bg-green-100 ppv2-text-green-800' : 'ppv2-bg-gray-100 ppv2-text-gray-800'"
                    >
                        {{ item.send ? $t('common.yes') : $t('common.no') }}
                    </span>
                </template>

                <!-- Custom cell: updated_at -->
                <template #column-updated_at="{ item }">
                    {{ formatDate(item.updated_at) }}
                </template>

                <!-- Actions -->
                <template #column-actions="{ item }">
                    <div v-if="canEditOrDelete(item)" class="ppv2-flex ppv2-items-center ppv2-justify-center ppv2-gap-3">
                        <a
                            :href="`/back-office/v3/communications/notifications/edit/${item.id}`"
                            class="ppv2-p-2 ppv2-text-black hover:ppv2-bg-black hover:ppv2-bg-opacity-5 ppv2-rounded-lg ppv2-transition-all"
                            :title="$t('common.edit')"
                        >
                            <SquarePen :size="18" />
                        </a>
                        <button
                            class="ppv2-p-2 ppv2-text-[#FF9C9C] hover:ppv2-bg-[#FF9C9C] hover:ppv2-bg-opacity-10 ppv2-rounded-lg ppv2-transition-all"
                            :title="$t('common.delete')"
                            @click="openDeleteModal(item)"
                        >
                            <Trash2 :size="18" />
                        </button>
                    </div>
                </template>
            </DataTable>

            <!-- Delete Confirmation Dialog -->
            <ConfirmationDialog
                :is-open="showDeleteModal"
                :title="$t('notifications_push.delete_modal.title')"
                :message="$t('notifications_push.delete_modal.message')"
                :confirm-text="$t('common.delete')"
                :cancel-text="$t('common.cancel')"
                confirm-button-variant="danger"
                @confirm="confirmDelete"
                @cancel="cancelDelete"
            />
        </div>
    </div>
</template>

<script>
import DataTable from '../../../Shared/DataTable.vue'
import Search from '../../Experience/components/Search.vue'
import ConfirmationDialog from '../../../Shared/ConfirmationDialog.vue'
import BaseButton from '../../../Shared/BaseButton.vue'
import { SquarePen, Trash2, Plus } from 'lucide-vue'
import normalizeSize from '../../../../utils/normalizeSize'
import normalizeFontSize from '../../../../utils/normalizeFontSize'
import ApiManager from './ApiManager'
import Header from '../../../Shared/Header.vue'
import FilterByType from '../../../Shared/FilterByType.vue'
import { getPerPage, setPerPage } from '../../../../utils/perPage'
export default {
    name: 'NotificationPushV3Index',

    components: {
        DataTable,
        Search,
        ConfirmationDialog,
        BaseButton,
        SquarePen,
        Trash2,
        Plus,
        Header,
        FilterByType,
    },

    data() {
        return {
            items: [],
            loading: false,
            currentPage: 1,
            perPage: getPerPage('notifications_push_items_per_page', 10),
            totalEntries: 0,
            searchQuery: '',
            sortKey: '',
            sortOrder: 'asc',
            searchTimeout: null,
            statusFilter: '',
            datatableKey: 0,
            showDeleteModal: false,
            itemToDelete: null,
        }
    },

    computed: {
        tableColumns() {
            return [
                {
                    key: 'system_title',
                    label: this.$t('notifications_push.columns.system_title'),
                    sortable: true,
                    type: 'text',
                },
                {
                    key: 'title_notif',
                    label: this.$t('notifications_push.columns.title_notif'),
                    sortable: true,
                    type: 'text',
                },
                {
                    key: 'desc_notif',
                    label: this.$t('notifications_push.columns.description'),
                    sortable: false,
                    type: 'text',
                },
                {
                    key: 'send_time',
                    label: this.$t('notifications_push.columns.send_time'),
                    sortable: true,
                    type: 'text',
                },
                {
                    key: 'end_date',
                    label: this.$t('notifications_push.columns.end_date'),
                    sortable: true,
                    type: 'date',
                },
                {
                    key: 'updated_at',
                    label: this.$t('notifications_push.columns.updated_at'),
                    sortable: true,
                    type: 'date',
                },
                {
                    key: 'send',
                    label: this.$t('notifications_push.columns.send'),
                    sortable: true,
                    type: 'boolean',
                },
                {
                    key: 'actions',
                    label: this.$t('common.actions'),
                    sortable: false,
                    type: 'actions',
                },
            ]
        },
        listApiUrl() {
            if (this.statusFilter === 'true') {
                return '/back-office/v3/communications/notifications/list?history=true'
            }
            if (this.statusFilter === 'false') {
                return '/back-office/v3/communications/notifications/list?history=false'
            }
            return '/back-office/v3/communications/notifications/list'
        },
        totalPages() {
            return Math.ceil(this.totalEntries / this.perPage)
        },
        statusOptions() {
            return [
                { value: '', label: this.$t('notifications_push.status.all') },
                { value: 'false', label: this.$t('notifications_push.status.current') },
                { value: 'true', label: this.$t('notifications_push.status.history') },
            ]
        },
    },

    mounted() {
        this.fetchData()
        // Remove white background from parent container for V3 layout
        const mainContainer = document.getElementById('main-container')
        if (mainContainer) {
            mainContainer.classList.add('product-v3-no-bg')
        }
    },

    beforeDestroy() {
        // Restore background when leaving the page
        const mainContainer = document.getElementById('main-container')
        if (mainContainer) {
            mainContainer.classList.remove('product-v3-no-bg')
        }
    },

    methods: {
        normalizeSize,
        normalizeFontSize,

        async fetchData() {
            this.loading = true
            try {
                const params = {
                    page: this.currentPage,
                    per_page: this.perPage,
                    search: this.searchQuery,
                    sort_by: this.sortKey,
                    sort_order: this.sortOrder,
                }
                const queryString = new URLSearchParams(params).toString()
                const separator = this.listApiUrl.includes('?') ? '&' : '?'
                const url = `${this.listApiUrl}${separator}${queryString}`

                const response = await fetch(url, {
                    method: 'GET',
                    headers: { 'Content-Type': 'application/json' },
                })

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`)
                }

                const json = await response.json()
                const _data = json.data

                if (_data.data && Array.isArray(_data.data)) {
                    this.items = _data.data
                    this.totalEntries = _data.total || _data.data.length
                } else if (Array.isArray(_data)) {
                    this.items = _data
                    this.totalEntries = _data.length
                } else {
                    this.items = []
                    this.totalEntries = 0
                }
            } catch (error) {
                console.error('Erreur chargement notifications:', error)
            } finally {
                this.loading = false
            }
        },

        handlePageChange(page) {
            this.currentPage = page
            this.fetchData()
        },
        handlePerPageChange(perPage) {
            this.perPage = Number(perPage)
            setPerPage('notifications_push_items_per_page', this.perPage)
            this.currentPage = 1
            this.fetchData()
        },

        onSearch(query) {
            this.searchQuery = query
            clearTimeout(this.searchTimeout)
            this.searchTimeout = setTimeout(() => {
                this.currentPage = 1
                this.fetchData()
            }, 500)
        },

        onSort(key) {
            if (this.sortKey === key) {
                this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc'
            } else {
                this.sortKey = key
                this.sortOrder = 'asc'
            }
            this.currentPage = 1
            this.fetchData()
        },

        handleStatusChange(value) {
            this.statusFilter = value
            this.datatableKey++
            this.currentPage = 1
            this.fetchData()
        },

        goToCreate() {
            window.Turbo.visit('/back-office/v3/communications/notifications/create')
        },

        formatDate(date) {
            if (!date) return ''
            const d = new Date(date)
            const year = d.getFullYear()
            const month = String(d.getMonth() + 1).padStart(2, '0')
            const day = String(d.getDate()).padStart(2, '0')
            return `${year}-${month}-${day}`
        },

        openDeleteModal(item) {
            this.itemToDelete = item
            this.showDeleteModal = true
        },

        cancelDelete() {
            this.showDeleteModal = false
            this.itemToDelete = null
        },

        async confirmDelete() {
            if (!this.itemToDelete) return

            try {
                const response = await ApiManager.destroy(this.itemToDelete.id)
                if (response.success) {
                    this.$toast.success(response.message || this.$t('notifications_push.messages.delete_success'))
                    this.fetchData()
                } else {
                    this.$toast.error(response.message || this.$t('notifications_push.messages.error'))
                }
            } catch (error) {
                console.error('Erreur suppression:', error)
                this.$toast.error(this.$t('notifications_push.messages.delete_error'))
            } finally {
                this.showDeleteModal = false
                this.itemToDelete = null
            }
        },
        canEditOrDelete(item) {
            const isSent = Boolean(Number(item.send))
            return !isSent
        },
    },
}
</script>

<style scoped lang="scss">
.notification-push-v3-container {
    padding: 0;
}
</style>

<style lang="scss">
#main-container.product-v3-no-bg {
    background: transparent !important;
    border-radius: 0px;
}

#main-container:has(.notification-push-v3-container) {
    background: transparent !important;
}

.noMarginInside * {
    margin: 0 !important;
}
</style>
